import { useEffect } from "react";

interface SeoHeadProps {
  title: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogImage?: string;
  twitterCard?: string;
  geoPosition?: string;
  geoPlacename?: string;
  geoRegion?: string;
  schema?: Record<string, unknown> | Record<string, unknown>[];
}

function resolveUrl(url: string) {
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  const base = "https://carreaux-bat.ch";
  return url.startsWith("/") ? `${base}${url}` : `${base}/${url}`;
}

function setMeta(name: string, content: string, isProperty = false) {
  const selector = isProperty
    ? `meta[property="${name}"]`
    : `meta[name="${name}"]`;
  let el = document.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    if (isProperty) el.setAttribute("property", name);
    else el.name = name;
    document.head.appendChild(el);
  }
  el.content = content;
}

function setCanonical(href: string) {
  let link = document.querySelector(
    'link[rel="canonical"]'
  ) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.rel = "canonical";
    document.head.appendChild(link);
  }
  link.href = href;
}

function removeCanonical() {
  const link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (link) link.remove();
}

function setSchema(schemaData: Record<string, unknown> | Record<string, unknown>[]) {
  let script = document.querySelector(
    'script[type="application/ld+json"]'
  ) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(schemaData);
}

export default function SeoHead({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogType = "website",
  ogImage,
  twitterCard = "summary_large_image",
  geoPosition,
  geoPlacename,
  geoRegion,
  schema,
}: SeoHeadProps) {
  useEffect(() => {
    document.title = title;
    if (description) setMeta("description", description);
    if (keywords) setMeta("keywords", keywords);
    if (geoPosition) setMeta("geo.position", geoPosition);
    if (geoPlacename) setMeta("geo.placename", geoPlacename);
    if (geoRegion) setMeta("geo.region", geoRegion);
    if (ogTitle) setMeta("og:title", ogTitle, true);
    if (ogDescription) setMeta("og:description", ogDescription, true);
    if (ogType) setMeta("og:type", ogType, true);
    if (ogImage) setMeta("og:image", resolveUrl(ogImage), true);
    if (twitterCard) setMeta("twitter:card", twitterCard);
    setMeta("last-modified", new Date().toUTCString());
    if (canonical) setCanonical(canonical);
    else removeCanonical();
    if (schema) setSchema(schema);
  }, [
    title,
    description,
    keywords,
    canonical,
    ogTitle,
    ogDescription,
    ogType,
    ogImage,
    twitterCard,
    geoPosition,
    geoPlacename,
    geoRegion,
    schema,
  ]);

  return null;
}